import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  Camera,
  CameraResultType,
  CameraSource,
  Photo,
} from '@capacitor/camera';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Storage } from '@capacitor/storage';
import { UserPhoto } from '../../interfaces/user';
import { Platform } from '@ionic/angular';
import { StorageService } from '../storage/storage.service';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import exifr from 'exifr' ;

@Injectable({
  providedIn: 'root',
})
export class PhotoService {
  public photos: UserPhoto;
  public uploadedImageForProfilePhoto = new Subject<any>();
  public uploadedImageForPaymentAttachment = new Subject<any>();
  public uploadedImageForLogin = new Subject<any>();
  public uploadedImageForCreateStudent = new Subject<any>();
  public uploadedImageForCreateSchool = new Subject<any>();
  public uploadedImageForDrivingSchool = new Subject<any>();
  public uploadedImageForDrivingSchoolLogo = new Subject<any>();

  private PHOTO_STORAGE: string = 'photos';
  private platform: Platform;

  constructor(platform: Platform, private storageService: StorageService) {
    this.platform = platform;
  }

  public async addNewToGallery(type) {
    this.PHOTO_STORAGE = type;
    // Take a photo
    const capturedPhoto = await Camera.getPhoto({
      resultType: CameraResultType.Uri,
      source: CameraSource.Camera,
      quality: 100,
      width: 1080,
      height: 1080,      
      allowEditing: true,
      correctOrientation: true,
      webUseInput: false
    });

    const savedImageFile = await this.savePicture(capturedPhoto);

    //console.log(capturedPhoto.exif);

    const exitfrData = await exifr.parse(savedImageFile.webviewPath, true);
    var imgDate;
    if (exitfrData.CreateDate === undefined || exitfrData.CreateDate === null) {
      imgDate = null;
      savedImageFile.validImage = true;
      //console.log("No Date Image", exitfrData);
    } else {
      imgDate = exitfrData.CreateDate;
      var now = new Date();
      var logs = new Date(imgDate);
      var diffMs = (new Date(now).getTime() - new Date(logs).getTime()) / 60000;
      if (diffMs > 60) {
        savedImageFile.base64Data = null;
        savedImageFile.blob = null;
        savedImageFile.filepath = null;
        savedImageFile.webviewPath = null;
        savedImageFile.validImage = false;
        //console.log("Invalid Image - old photo", exitfrData);
      }
      else {
        savedImageFile.validImage = true;
        //console.log("Valid Image - new photo", exitfrData);
      }
    }
   
    if (type == 'student_photo') {
      this.uploadedImageForProfilePhoto.next(savedImageFile);
    }

    if (type == 'payment_attachment') {
      this.uploadedImageForPaymentAttachment.next(savedImageFile);
    }

    if (type == 'login') {
      this.uploadedImageForLogin.next(savedImageFile);
    }

    if (type == 'createStudent') {
      this.uploadedImageForCreateStudent.next(savedImageFile);
    }

    if (type == 'createSchool') {
      this.uploadedImageForCreateSchool.next(savedImageFile);
    }

    if (type == 'school_payment_attachment') {
      this.uploadedImageForDrivingSchool.next(savedImageFile);
    }

    if (type == 'company_photo') {
      this.uploadedImageForDrivingSchoolLogo.next(savedImageFile);
    }  

    //this.storageService.remove(this.PHOTO_STORAGE);
    //this.storageService.set(this.PHOTO_STORAGE, JSON.stringify(savedImageFile));
  }

  private async savePicture(cameraPhoto: Photo) {
    // Convert photo to base64 format, required by Filesystem API to save
    const base64Data = await this.readAsBase64(cameraPhoto);

    const imageData = await cameraPhoto.exif;

    // Write the file to the data directory
    const fileName = new Date().getTime() + '.jpeg';
    const blobData = await this.readAsBlob(cameraPhoto, fileName);
    const savedFile = await Filesystem.writeFile({
      path: fileName,
      data: base64Data,
      directory: Directory.Data
    });

    // Use webPath to display the new image instead of base64 since it's
    // already loaded into memory
    if (this.platform.is('hybrid')) {
      // Display the new image by rewriting the 'file://' path to HTTP
      // Details: https://ionicframework.com/docs/building/webview#file-protocol
      return {
        filepath: savedFile.uri,
        webviewPath: Capacitor.convertFileSrc(savedFile.uri),
        blob: blobData,
        base64Data: base64Data,
        validImage: null
      };
    } else {
      // Use webPath to display the new image instead of base64 since it's
      // already loaded into memory
      return {
        filepath: fileName,
        webviewPath: cameraPhoto.webPath,
        blob: blobData,        
        base64Data: base64Data,
        validImage: null
      };
    }
  }

  private async readAsBlob(cameraPhoto: any, fileName) {
    // Fetch the photo, read as a blob, then convert to base64 format
    const response = await fetch(cameraPhoto.webPath!);
    const blob = await response.blob();

    var file = new File([blob], fileName, {
      type: blob.type,
      lastModified: new Date().getTime(),         
    });

    return file;
  }

  removePhotoFromFileSystem(file) {
    var photo = {
      path: file,
      directory: Directory.Data,
    };
    Filesystem.deleteFile(photo);
  }

  async removeAllPhotos() {
    const info = await Filesystem.stat({
      path: '',
      directory: Directory.Data,
    });
    //console.log('Stat Info: ', info);
  }

  private async readAsBase64(cameraPhoto: Photo) {
    // Fetch the photo, read as a blob, then convert to base64 format
    const response = await fetch(cameraPhoto.webPath!);
    const blob = await response.blob();

    return (await this.convertBlobToBase64(blob)) as string;
  }

  public async loadSaved(storagePhoto) {
    const readFile = await Filesystem.readFile({
      path: storagePhoto.filepath,
      directory: Directory.Data,
    });
    return (storagePhoto.webviewPath = `data:image/jpeg;base64,${readFile.data}`);
  }

  convertBlobToBase64 = (blob: Blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.readAsDataURL(blob);
    });
}
